import React, { createContext, useContext, useReducer, useEffect } from 'react';

const UserDataContext = createContext();

const initialState = {
  users: [],
  pendingRegistrations: [],
  currentUser: null,
  loading: false
};

function userDataReducer(state, action) {
  switch (action.type) {
    case 'ADD_USER_REGISTRATION':
      return {
        ...state,
        pendingRegistrations: [...state.pendingRegistrations, action.payload]
      };
    
    case 'APPROVE_USER':
      const approvedUser = state.pendingRegistrations.find(u => u.id === action.payload);
      return {
        ...state,
        users: [...state.users, { ...approvedUser, status: 'approved', approvedAt: new Date().toISOString() }],
        pendingRegistrations: state.pendingRegistrations.filter(u => u.id !== action.payload)
      };
    
    case 'REJECT_USER':
      return {
        ...state,
        pendingRegistrations: state.pendingRegistrations.filter(u => u.id !== action.payload)
      };
    
    case 'LOAD_DATA':
      return {
        ...state,
        users: action.payload.users || [],
        pendingRegistrations: action.payload.pendingRegistrations || []
      };
    
    case 'SET_CURRENT_USER':
      return {
        ...state,
        currentUser: action.payload
      };
    
    default:
      return state;
  }
}

export function UserDataProvider({ children }) {
  const [state, dispatch] = useReducer(userDataReducer, initialState);

  // Load data from localStorage on start
  useEffect(() => {
    const savedUsers = JSON.parse(localStorage.getItem('stoneforex_users') || '[]');
    const savedPending = JSON.parse(localStorage.getItem('stoneforex_pending') || '[]');
    
    dispatch({ 
      type: 'LOAD_DATA', 
      payload: { 
        users: savedUsers, 
        pendingRegistrations: savedPending 
      } 
    });
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('stoneforex_users', JSON.stringify(state.users));
    localStorage.setItem('stoneforex_pending', JSON.stringify(state.pendingRegistrations));
  }, [state.users, state.pendingRegistrations]);

  const registerUser = (userData) => {
    const newUser = {
      id: Date.now(),
      ...userData,
      status: 'pending',
      registeredAt: new Date().toISOString()
    };
    
    dispatch({ type: 'ADD_USER_REGISTRATION', payload: newUser });
    return newUser;
  };

  const approveUser = (userId) => {
    dispatch({ type: 'APPROVE_USER', payload: userId });
  };

  const rejectUser = (userId) => {
    dispatch({ type: 'REJECT_USER', payload: userId });
  };

  const loginUser = (email, password) => {
    const user = state.users.find(u => u.email === email && u.password === password && u.status === 'approved');
    if (user) {
      dispatch({ type: 'SET_CURRENT_USER', payload: user });
      return user;
    }
    return null;
  };

  const value = {
    ...state,
    registerUser,
    approveUser,
    rejectUser,
    loginUser
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within UserDataProvider');
  }
  return context;
};
